import { getPermissions } from "./permissions";

export const STORAGE_KEY = "ap-liu";

/**
 * Safe JSON.parse that tolerates null/empty/invalid JSON.
 * @template T
 * @param {string|null} json
 * @returns {T|null}
 */
function safeParse(json) {
  if (!json || json.trim() === "") {
    return null;
  }
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Returns the raw user object from localStorage, or null.
 * @returns {any|null}
 */
export function getUserRaw() {
  return safeParse(localStorage.getItem(STORAGE_KEY));
}

/**
 * Overwrite the user object in localStorage.
 * @param {any} obj
 */
export function setUserRaw(obj) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(obj ?? {}));
}

/**
 * Shallow-merge patch into the stored user object and persist.
 * @param {Record<string, any>} patch
 * @returns {any} next stored object
 */
export function updateUser(patch) {
  const cur = getUserRaw() ?? {};
  const next = { ...cur, ...patch };
  setUserRaw(next);
  return next;
}

/**
 * @returns {string} First name of logged in User.
 */
export function getUserFirstName() {
  const item = getUserRaw();
  return item?.FirstName ?? "";
}

/**
 * @returns {string} Full name of logged in User.
 */
export function getUserFullName() {
  const item = getUserRaw();
  if (!item) return "";
  const first = item.FirstName ?? "";
  const last = item.LastName ?? "";
  return `${first} ${last}`.trim();
}

/**
 * @returns {string} Email address of logged in User.
 */
export function getUserEmail() {
  const item = getUserRaw();
  return item?.Email ?? "";
}

/**
 * @returns {string} Reseller name of logged in User.
 */
export function getUserResellerName() {
  const item = getUserRaw();
  return item?.ResellerName ?? "";
}

/**
 * @returns {string} Reseller Reference of logged in User.
 */
export function getUserResellerReference() {
  const item = getUserRaw();
  return item?.ResellerReference ?? "";
}

/**
 * @readonly
 * @enum {number}
 */
export const ResellerType = {
  Invalid: -1,
  iTEL: 0,
  Billable: 1,
  NonBillable: 2,
};

/**
 * @returns {number} Reseller Type of logged in User.
 */
export function getUserResellerType() {
  const item = getUserRaw();
  return typeof item?.ResellerType === "number" ? item.ResellerType : -1;
}

/**
 * @returns {number} Login Type of logged in User.
 */
export function getLoginType() {
  const item = getUserRaw();
  return typeof item?.LoginType === "number" ? item.LoginType : -1;
}

/**
 * @returns {number} Timestamp of logged in User.
 */
export function getTimestamp() {
  const item = getUserRaw();
  return typeof item?.Timestamp === "number" ? item.Timestamp : -1;
}

/** @returns {boolean} */
export function getDisableUserModificationFlag() {
  const item = getUserRaw();
  return !!item?.DisableUserModification;
}

/**
 * Returns Permissions: [{ Name, DisplayName, Description }]
 * @returns {{ Name: string, Description: string }[]}
 */
export function getResellerUserPermissions() {
  return getPermissions();
}

export function updateTimestamp(value) {
  updateUser({ Timestamp: value });
}